import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="map-toggle"
export default class extends Controller {
  static targets = [
    "mapOverlay",
    "mapButton",
    "mapIcon",
    "photoIcon",
    "infoPanel",
    "helpPanel",
    "mapCard",
    "pinButton",
    "mapControls",
    "mediaButton",
  ];
  static values = {
    pinPlaced: { type: Boolean, default: false },
    autoHide: { type: Boolean, default: false },
  };

  connect() {
    // Listen for pin placement event from map controller
    document.addEventListener(
      "map:pin-placed",
      this.handlePinPlaced.bind(this)
    );

    // Listen for map open event
    document.addEventListener("map-toggle:open", this.handleMapOpen.bind(this));

    // Set initial button state
    this.updateButtonState();
  }

  disconnect() {
    // Clean up the event listeners
    document.removeEventListener(
      "map:pin-placed",
      this.handlePinPlaced.bind(this)
    );

    document.removeEventListener(
      "map-toggle:open",
      this.handleMapOpen.bind(this)
    );
  }

  // Handle map open event
  handleMapOpen() {
    if (this.mapOverlayTarget.classList.contains("hidden")) {
      this.showMap();
    }
  }

  // Show the map overlay
  showMap() {
    // First make it visible but still transparent
    this.mapOverlayTarget.classList.remove("hidden");
    this.mapControlsTarget.classList.add("hidden");
    // Force a reflow to ensure the transition works
    void this.mapOverlayTarget.offsetWidth;

    // Now make it fully visible with transition
    this.mapOverlayTarget.classList.add("opacity-100");
    this.mapCardTarget.classList.remove("translate-y-8");

    document.body.classList.add("overflow-hidden");

    // Wait until animation completes to fully initialize map
    // This helps prevent the glitchy zoom effect
    setTimeout(() => {
      // Dispatch a custom event that the map is fully visible
      const event = new CustomEvent("map:container-visible");
      document.dispatchEvent(event);

      // Trigger resize event to fix map rendering issues
      window.dispatchEvent(new Event("resize"));

      // Force Leaflet to recalculate its container size
      const mapElement = document.querySelector("[data-map-target='map']");
      if (mapElement && mapElement._leaflet_id) {
        const leafletMap =
          window.L.map._layers && window.L.map._layers[mapElement._leaflet_id];
        if (leafletMap) {
          leafletMap.invalidateSize(true);
        }
      }
    }, 350); // Slightly longer timeout to ensure animation is complete

    // Update button to show pin icon (disabled if no pin)
    this.showPinButton();
  }

  // Hide the map overlay
  hideMap() {
    // Start the fade out animation
    this.mapOverlayTarget.classList.remove("opacity-100");
    this.mapCardTarget.classList.add("translate-y-8");

    // Wait for the animation to complete before hiding
    setTimeout(() => {
      this.mapOverlayTarget.classList.add("hidden");
      this.mapControlsTarget.classList.remove("hidden");
      document.body.classList.remove("overflow-hidden");
    }, 300); // Match the duration in your CSS

    // Return to map button when map is closed
    this.showMapButton();
  }

  // Show map button (when map is closed)
  showMapButton() {
    this.mapButtonTarget.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" />
      </svg>
    `;
    this.mapButtonTarget.classList.remove("bg-success");
    this.mapButtonTarget.classList.add("bg-primary");
    this.mapButtonTarget.disabled = false;
    this.mapButtonTarget.setAttribute(
      "data-action",
      "click->map-toggle#showMap"
    );
  }

  hideMapButton() {
    this.mapButtonTarget.classList.add("hidden");
  }
  // Show pin button (when map is open)
  showPinButton() {
    // If pin is placed, enable button for submission
    if (this.pinPlacedValue) {
      console.log("showPinButton");
      this.pinButtonTarget.classList.remove("bg-primary");
      this.pinButtonTarget.classList.add("bg-success");
      this.pinButtonTarget.disabled = false;
      this.pinButtonTarget.setAttribute(
        "data-action",
        "click->map-toggle#submitForm"
      );
    } else {
      // If no pin is placed, disable button
      this.pinButtonTarget.classList.remove("bg-success");
      this.pinButtonTarget.classList.add("bg-primary");
      this.pinButtonTarget.disabled = true;
      // No data-action needed for disabled button
    }
  }

  // Update button based on current state
  updateButtonState() {
    if (this.mapOverlayTarget.classList.contains("hidden")) {
      // Map is closed - show map button
      this.showMapButton();
    } else {
      // Map is open - show pin button (enabled/disabled based on pin status)
      this.showPinButton();
    }
  }

  // Toggle the info panel visibility
  toggleInfoPanel() {
    if (this.hasInfoPanelTarget) {
      this.infoPanelTarget.classList.toggle("hidden");
    }
  }

  // Alias for toggleInfoPanel to match the data-action attribute
  toggleInfo() {
    this.toggleInfoPanel();
  }

  // Toggle the help panel visibility
  toggleHelpPanel() {
    if (this.hasHelpPanelTarget) {
      this.helpPanelTarget.classList.toggle("hidden");
    }
  }

  // Alias for toggleHelpPanel to match the data-action attribute
  toggleHelp() {
    this.toggleHelpPanel();
  }

  // Handle the pin placed event from the map controller
  handlePinPlaced(event) {
    this.pinPlacedValue = true;

    // Update the button to enabled state (still pin icon)
    this.showPinButton();

    // Only hide the map if autoHide is true
    if (this.hasAutoHideValue && this.autoHideValue) {
      this.hideMap();
    }
  }

  // Submit the form
  submitForm() {
    // Find and submit the form
    const form = this.element.querySelector("form");
    if (form) {
      form.requestSubmit();
    }
  }

  // Open the modal
  openModal() {
    document.getElementById("spot-modal").checked = true;
  }

  // Handle help option selection and open email client
  openHelpEmail(event) {
    const option = event.currentTarget.dataset.helpOption;
    let subject = "";
    let body =
      "Hello,\n\nI encountered an issue with WaveGuessr and would like to report it.\n\nProblem description:\n";

    switch (option) {
      case "media":
        subject = "WaveGuessr - Media not displaying (video/photo)";
        break;
      case "spot":
        subject = "WaveGuessr - The spot is wrong";
        break;
      case "other":
        subject = "WaveGuessr - Other issue";
        break;
    }

    const mailtoLink = `mailto:thomas@thesurfkit.com?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);

    // Hide the help panel after opening email
    if (this.hasHelpPanelTarget) {
      this.helpPanelTarget.classList.add("hidden");
    }
  }
}
